<template>
  <div>
    <b-card no-body class="m-0">
      <b-row class="d-flex justify-content-between gap-1">
        <b-col class="mt-1 d-flex pagination-button">
          <v-select
            v-model="perPage"
            :clearable="false"
            class="per-page-selector ml-1"
            style="width: 100px"
            :options="['5', '8', '10', '20', '50']"
            @input="reloadPageOne"
          />
          <b-button
            variant="primary"
            class="admin-user-primary-btn ml-1"
            @click="openCreateModal"
          >
            Add Admin User
          </b-button>
        </b-col>
        <div class="ml-1 mt-1 mr-1">
          <b-form-input
            v-model="search"
            placeholder="Search admin users..."
            class="mr-1"
            style="width: 220px"
            @input="debouncedSearch"
          />
        </div>
      </b-row>

      <div class="m-2">
        <b-table
          :busy="isLoading"
          :items="rows"
          responsive
          :fields="tableColumns"
          show-empty
          empty-text="No admin users found"
        >
          <template #table-busy>
            <div class="text-center text-secondary my-2">
              <b-spinner class="align-middle" />
            </div>
          </template>
          <template #cell(no)="data">
            <span>{{ String(data.item.no).padStart(2, '0') }}</span>
          </template>
          <template #cell(status)="data">
            <b-badge :variant="data.item.status_value === 1 ? 'light-success' : 'light-secondary'">
              {{ data.item.status }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <row-actions
              @edit="openEditModal(data.item)"
              @delete="confirmDelete(data.item)"
            />
          </template>
        </b-table>

        <div class="d-flex justify-content-between align-items-center px-1 pb-1">
          <AdminPaginationBar
            :page="currentPage"
            :per-page="perPageNum"
            :total="totalRows"
            @change="onPageChange"
          />
        </div>
      </div>
    </b-card>

    <b-modal
      v-model="staffModal"
      hide-footer
      no-close-on-backdrop
      centered
      size="lg"
      :title="isEdit ? 'Edit Admin User' : 'Add Admin User'"
      @hidden="resetModal"
    >
      <validation-observer ref="staffForm">
        <b-row>
          <b-col md="6" cols="12">
            <mypoda-form-field label="First name" name="First name" rules="required|max:255" required-mark>
              <template #default="{ state, inputId }">
                <b-form-input :id="inputId" v-model="form.f_name" :state="state" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6" cols="12">
            <mypoda-form-field label="Last name" name="Last name" rules="required|max:255" required-mark>
              <template #default="{ state, inputId }">
                <b-form-input :id="inputId" v-model="form.l_name" :state="state" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6" cols="12">
            <mypoda-form-field label="Email" name="Email" rules="required|email|max:255" required-mark>
              <template #default="{ state, inputId }">
                <b-form-input :id="inputId" v-model="form.email" type="email" :state="state" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6" cols="12">
            <mypoda-form-field label="Phone" name="Phone" rules="required|phone_10" required-mark>
              <template #default="{ state, inputId }">
                <b-form-input
                  :id="inputId"
                  :value="form.phone"
                  :state="state"
                  inputmode="numeric"
                  maxlength="10"
                  placeholder="10-digit phone number"
                  @input="onPhoneInput"
                />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6" cols="12">
            <mypoda-form-field
              :label="isEdit ? 'Password (leave blank to keep)' : 'Password'"
              name="Password"
              :rules="isEdit ? 'min:8|max:255' : 'required|min:8|max:255'"
              :required-mark="!isEdit"
            >
              <template #default="{ state, inputId }">
                <b-form-input :id="inputId" v-model="form.password" type="password" :state="state" />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col md="6" cols="12">
            <mypoda-form-field label="Role" name="Role" rules="required" required-mark>
              <template #default="{ state, inputId }">
                <b-form-select
                  :id="inputId"
                  v-model="form.admin_role_id"
                  :options="roleOptions"
                  :state="state"
                />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col cols="12" class="d-flex align-items-center">
            <b-form-checkbox v-model="form.status" switch>
              Active
            </b-form-checkbox>
          </b-col>
          <b-col cols="12" class="d-flex justify-content-start mt-2">
            <b-button variant="primary" class="mr-1" :disabled="saving" @click.prevent="submitForm">
              <b-spinner v-if="saving" small class="mr-50" />
              {{ isEdit ? 'Update' : 'Create' }}
            </b-button>
            <b-button variant="outline-secondary" @click="closeModal">
              Cancel
            </b-button>
          </b-col>
        </b-row>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormCheckbox,
  BFormInput,
  BFormSelect,
  BRow,
  BSpinner,
  BTable,
  BModal,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import {
  applyListPagination,
  debounce,
  unwrapAdminResponse,
  unwrapPaginatedList,
} from '@/utils/adminApiHelpers'
import { confirmAction, notifyError, notifySuccess } from '@/utils/uiActions'
import RowActions from '@/components/admin/RowActions.vue'
import MypodaFormField from '@/components/mypoda/MypodaFormField.vue'

const emptyForm = () => ({
  f_name: '',
  l_name: '',
  email: '',
  phone: '',
  password: '',
  admin_role_id: null,
  status: true,
})

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormCheckbox,
    BFormInput,
    BFormSelect,
    BRow,
    BSpinner,
    BTable,
    BModal,
    ValidationObserver,
    vSelect,
    MypodaFormField,
    RowActions,
    AdminPaginationBar,
  },
  data() {
    return {
      isLoading: false,
      saving: false,
      staffModal: false,
      isEdit: false,
      selectedId: null,
      search: '',
      currentPage: 1,
      perPage: '8',
      totalRows: 0,
      rows: [],
      roles: [],
      form: emptyForm(),
      tableColumns: [
        { key: 'no', label: 'NO' },
        { key: 'name', label: 'NAME' },
        { key: 'email', label: 'EMAIL' },
        { key: 'phone', label: 'PHONE' },
        { key: 'role_name', label: 'ROLE' },
        { key: 'status', label: 'STATUS' },
        { key: 'actions', label: 'ACTIONS' },
      ],
    }
  },
  computed: {
    perPageNum() {
      return parseInt(this.perPage, 10) || 8
    },
    roleOptions() {
      return [
        { value: null, text: 'Select role', disabled: true },
        ...this.roles.map(role => ({ value: role.id, text: role.name || `Role #${role.id}` })),
      ]
    },
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.currentPage = 1
      this.fetchStaff()
    }, 400)
    this.fetchRoles()
    this.fetchStaff()
  },
  methods: {
    onPhoneInput(value) {
      this.form.phone = String(value || '').replace(/\D/g, '').slice(0, 10)
    },
    reloadPageOne() {
      this.currentPage = 1
      this.fetchStaff()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchStaff()
    },
    async fetchRoles() {
      try {
        const res = await adminPanelApi.staffRoles()
        const payload = unwrapAdminResponse(res)
        this.roles = payload.roles || []
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to load admin roles.')
      }
    },
    mapStaffRow(item) {
      return {
        ...item,
        name: item.name || `${item.f_name || ''} ${item.l_name || ''}`.trim(),
        role_name: item.role_name || item.role?.name || '',
        status: item.status || (item.status_value === 1 ? 'Active' : 'Inactive'),
      }
    },
    async fetchStaff() {
      this.isLoading = true
      try {
        const res = await adminPanelApi.staff({
          page: this.currentPage,
          per_page: this.perPageNum,
          search: this.search.trim() || undefined,
        })
        const payload = unwrapAdminResponse(res)
        const { items, total } = unwrapPaginatedList(res, ['staff'])
        const list = items.length ? items : (payload.staff || [])
        this.rows = list.map(item => this.mapStaffRow(item))
        applyListPagination(this, res, payload)
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to load admin users.')
        this.rows = []
        this.totalRows = 0
      } finally {
        this.isLoading = false
      }
    },
    openCreateModal() {
      this.isEdit = false
      this.selectedId = null
      this.form = emptyForm()
      this.staffModal = true
    },
    openEditModal(item) {
      this.isEdit = true
      this.selectedId = item.id
      this.form = {
        f_name: item.f_name || '',
        l_name: item.l_name || '',
        email: item.email || '',
        phone: String(item.phone || '').replace(/\D/g, '').slice(0, 10),
        password: '',
        admin_role_id: item.admin_role_id || null,
        status: item.status_value === 1,
      }
      this.staffModal = true
    },
    closeModal() {
      this.staffModal = false
    },
    resetModal() {
      this.isEdit = false
      this.selectedId = null
      this.form = emptyForm()
      if (this.$refs.staffForm) {
        this.$refs.staffForm.reset()
      }
    },
    async submitForm() {
      const valid = await this.$refs.staffForm.validate()
      if (!valid) return

      this.saving = true
      const payload = {
        f_name: this.form.f_name.trim(),
        l_name: this.form.l_name.trim(),
        email: this.form.email.trim(),
        phone: this.form.phone.trim(),
        admin_role_id: Number(this.form.admin_role_id),
        status: this.form.status ? 1 : 0,
      }
      if (this.form.password) {
        payload.password = this.form.password
      }

      try {
        const listPageBeforeSave = this.currentPage
        if (this.isEdit && this.selectedId) {
          await adminPanelApi.updateStaff(this.selectedId, payload)
          notifySuccess(this, 'Admin user updated successfully.')
        } else {
          if (!payload.password) {
            notifyError(this, 'Password is required for new admin users.')
            return
          }
          await adminPanelApi.createStaff(payload)
          notifySuccess(this, 'Admin user created successfully.')
          this.currentPage = 1
        }
        this.closeModal()
        // Watcher refetches when page changes; only refetch here if page stayed the same.
        if (this.isEdit || listPageBeforeSave === 1) {
          await this.fetchStaff()
        }
      } catch (e) {
        const apiErrors = e.response?.data?.errors
        const firstFieldError = apiErrors && Object.values(apiErrors).flat()[0]
        notifyError(this, firstFieldError || e.response?.data?.message || 'Unable to save admin user.')
      } finally {
        this.saving = false
      }
    },
    confirmDelete(item) {
      confirmAction(
        this,
        {
          title: 'Delete admin user?',
          text: `Delete "${item.name}"? This cannot be undone.`,
          confirmButtonText: 'Delete',
        },
        async () => {
          try {
            await adminPanelApi.deleteStaff(item.id)
            notifySuccess(this, 'Admin user deleted successfully.')
            if (this.rows.length === 1 && this.currentPage > 1) {
              this.currentPage -= 1
            }
            await this.fetchStaff()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to delete admin user.')
          }
        },
      )
    },
  },
}
</script>

<style scoped>
.admin-user-primary-btn {
  background-color: #011425 !important;
  border-color: #011425 !important;
}

.admin-user-primary-btn:hover,
.admin-user-primary-btn:focus,
.admin-user-primary-btn:active,
.admin-user-primary-btn:not(:disabled):not(.disabled):active {
  background-color: #011425 !important;
  border-color: #011425 !important;
}
</style>
